import { ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { RouterOutlet, Router, NavigationEnd, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';

import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

import { LoaderService } from './services/loader.service';
import { CoreService } from './services/core.service';
import { environment } from '../environments/environment';
import {
  PublioPerceptionIqNotificationService
} from './services/publio-perception-iq-notification.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    CommonModule,
    MatSidenavModule,
    MatToolbarModule,
    MatIconModule,
    MatListModule,
    MatButtonModule,
    RouterModule,
    MatProgressSpinnerModule,
    MatMenuModule,
    MatTooltipModule,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent {

  isDev = !environment.isProduction;

  @ViewChild('dashboardMenuTrigger')
  dashboardMenuTrigger!: MatMenuTrigger;



  @ViewChild('drawer') drawer: any;
  opened = false;
  loading$: Observable<boolean>;
  user: any = JSON.parse(localStorage.getItem('userInfo') || '{}');
  isDashboardRoute = false;
  isSmallScreen = false;
  isMediumScreen = false;

  get isLoggedIn(): boolean {
    return !!localStorage.getItem('userInfo');
  }

  currentYear = new Date().getFullYear();

  constructor(
    public router: Router,
    private coreService: CoreService,
    private loaderService: LoaderService,
    private cdr: ChangeDetectorRef,
    public readonly perceptionIqNotificationService:
      PublioPerceptionIqNotificationService
  ) {
    this.loading$ = this.loaderService.loading$;
  }

  ngOnInit(): void {
    this.updateScreenFlags();
    this.perceptionIqNotificationService.initialize();

    // Initial redirect on app load
    // if (!this.isLoggedIn && !this.router.url.startsWith('/login')) {
    //   this.router.navigate(['/login']);
    // }

    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe((e: NavigationEnd) => {
        this.isDashboardRoute = e.urlAfterRedirects.startsWith('/dashboard');
        this.user = JSON.parse(localStorage.getItem('userInfo') || '{}');

        // If user logs out (userInfo cleared) during navigation, force to /login
        if (!this.isLoggedIn && !e.urlAfterRedirects.startsWith('/login')) {
          this.router.navigate(['/login']);
        }

        if (this.isSmallScreen) {
          this.drawer.close();
        }

        const companyID = this.user?.companyID;
        if (companyID) {
          this.fetchLatestAlertsAndPosts(companyID);
        }

        this.cdr.detectChanges();
      });

    window.addEventListener('resize', () => {
      this.updateScreenFlags();
      this.cdr.detectChanges();
    });
  }

  private lastFetchTime = 0;
  private fetchLatestAlertsAndPosts(companyID: number | string): void {
    const now = Date.now();
    // Throttle to once every 10 seconds to avoid spamming the backend during rapid tab changes
    if (now - this.lastFetchTime < 10000) {
      return;
    }
    this.lastFetchTime = now;

    this.coreService.getLatestAlerts(companyID).subscribe();
    this.coreService.getLatestPosts(companyID).subscribe();
  }

  ngAfterViewInit() {
    this.loading$.subscribe(() => {
      this.cdr.detectChanges();
    });
  }

  onViewChange(view: string) {
    this.router.navigate(['/dashboard', view]);
  }

  get userRole(): string {
    return this.user?.userGroup || '';
  }

  private updateScreenFlags(): void {
    const width = window.innerWidth;
    this.isSmallScreen = width < 550;
    // this.isMediumScreen = width >= 550 && width < 1024;

    if (this.isSmallScreen) {
      this.opened = false;       // < 550px: mobile overlay, hidden       // 550–1023px: side, icon-only
    } else {
      this.opened = false;       // >= 1024px: side, fully expanded
    }
  }

  toggleDrawer() {
    this.opened = !this.opened;
  }

  private getCloudBaseUrl(): string {
    return environment.cloudUrl;
  }

  private getCloudCompanyUid(): string | null {
    const storedCompany = localStorage.getItem('company');

    let storedCompanyUid: string | null = null;

    if (storedCompany) {
      try {
        const company = JSON.parse(storedCompany);
        storedCompanyUid = company?.uid || company?.company_uid || null;
      } catch {
        storedCompanyUid = storedCompany;
      }
    }

    return (
      localStorage.getItem('cloud_company_uid') ||
      localStorage.getItem('company_uid') ||
      storedCompanyUid ||
      this.user?.company?.uid ||
      this.user?.company_uid ||
      this.user?.companyUid ||
      this.user?.companyUID ||
      null
    );
  }

  private clearLocalSession(): void {
    localStorage.removeItem('userInfo');
    localStorage.removeItem('latestAlerts');
    localStorage.removeItem('latestPosts');

    Object.keys(localStorage).forEach((key) => {
      if (
        key.startsWith('reports_visible_columns:') ||
        key.startsWith('qb_visible_columns:')
      ) {
        localStorage.removeItem(key);
      }
    });
  }

  logout(): void {
    const companyUid = this.getCloudCompanyUid();

    this.coreService.postData('logout', {}).subscribe({
      next: () => {
        this.clearLocalSession();

        if (companyUid) {
          window.location.href = `${this.getCloudBaseUrl()}/app/company/${companyUid}`;
          return;
        }

        this.router.navigate(['/login']);
      },
      error: () => {
        this.clearLocalSession();

        if (companyUid) {
          window.location.href = `${this.getCloudBaseUrl()}/app/company/${companyUid}`;
          return;
        }

        this.router.navigate(['/login']);
      },
    });
  }

  openDashboardMenu(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.dashboardMenuTrigger.openMenu();
  }
  

}
